import {
    Add,
    FilterAltOffOutlined,
    FilterAltOutlined,
    HorizontalRuleOutlined,
    TuneOutlined,
    VisibilityOutlined,
} from "@mui/icons-material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { green, grey, purple } from "@mui/material/colors";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AppContext } from "../../../Utilities/AppContext";
import { GET } from "../../../Utilities/BaseService";
import {
    filterSelectionMessage,
    hasValue,
    noDataMessage,
} from "../../../Utilities/constants";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import RuleView from "../../Connection/Components/RuleView";
import { getReportTemplates } from "../../Payment/Payment";
import { currencyFormatter } from "../../Reading/Bill/Cells/Last12Summary";
import BaseTable from "../../Table/BaseTable";
import SearchBar from "../../Table/Components/SearchBar";
import { getSelectedFilters } from "../Property/Property";
import MeterReplacement from "./MeterReplacement";
import PreviousMeter from "./PreviousMeter";
import { UnitCorrection } from "./UnitCorrection";

type Filter = {
    search: string;
    customer: number | "" | "all";
    district: number | "" | "all";
    village: number | "" | "all";
    hamlet: number | "" | "all";
    powerstation: number | "" | "all";
    property: number | "" | "all";
    connectionType: number[];
    connectionDateStart: string;
    connectionDateEnd: string;
    status: "active" | "inactive" | "replaced" | "";
    id?: number;
    selectedOrder: string;
    selectedColumn: string;
};

const initialFilter: Filter = {
    search: "",
    customer: "",
    district: "",
    village: "",
    hamlet: "",
    powerstation: "",
    property: "",
    connectionType: [],
    connectionDateStart: "",
    connectionDateEnd: "string",
    status: "active",
    selectedOrder: "",
    selectedColumn: "",
};

const initialClear = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    property: false,
    connectionType: false,
    priceType: false,
    search: false,
    status: false,
};

const Meter = () => {
    const [unitCorrectionOpen, setUnitCorrectionOpen] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeUnitchargeRuleRow, setActiveUnitChargeRuleRow] =
        useState<any>(null);
    const [rowsCount, setRowsCount] = useState(0);
    const [pagination, setPagination] = useState<any>({
        page: 0,
        limit: 100,
    });

    const handleViewRule = (row: any) => {
        setActiveUnitChargeRuleRow(row);
    };
    const [activeRow, setActiveRow] = useState<any>(null);
    const [filter, setFilter] = useState<any>(initialFilter);
    const [feedback, setFeedback] = useState("");
    const [reportTemplates, setReportTemplates] = useState<string[]>([]);
    const [showReplacementSlip, setShowReplacementSlip] = useState<
        null | number
    >(null);
    const [showPrevMeter, setShowPrevMeter] = useState(false);
    const [clearAll, setClearAll] = useState(initialClear);
    const [changeCount, setChangeCount] = useState(0);
    const [meterId, setMeterId] = useState<null | number>(null);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [intermediateFilter, setIntermediateFilter] =
        useState<Filter>(initialFilter);

    const columnOptions = [
        { label: "ID", value: "id" },
        { label: "Customer Name", value: "name" },
        { label: "Meter No", value: "meterNo" },
        { label: "Connection Date", value: "connectionDate" },
        { label: "None", value: "none" },
    ];

    const orderOptions = [
        { label: "Ascending", value: "ASC" },
        { label: "Descending", value: "DESC" },
        { label: "None", value: "none" },
    ];

    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));
    const { user } = useContext(AppContext);
    const isReplaceMeterOps = user?.ops?.includes("REPLACE METER");
    const isUnitCorrectionOps = user?.ops?.includes("UNIT CORRECTION");

    const location = useLocation();

    const getMeter = () => {
        setIsLoading(true);

        const params = Object.fromEntries(
            Object.entries(filter).filter(([k, v]) => v && v !== "all")
        );
        return GET("/customer/meter", {
            page: pagination.page + 1,
            limit: pagination.limit,
            ...params,
        });
    };

    const { data, isError, isSuccess } = useQuery(
        ["meter", pagination.page, pagination.limit, filter],
        getMeter,
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                setIsLoading(false);

                if (res.data) {
                    setRowsCount(res.data.count);

                    const _selectedFills = getSelectedFilters(
                        res.data.rows[0],
                        ["district", "village", "hamlet", "powerstation"],
                        filter,
                        {
                            district: { label: "District", valueKey: "name" },
                            village: { label: "Village", valueKey: "name" },
                            hamlet: { label: "Hamlet", valueKey: "name" },
                            powerstation: {
                                label: "Power Station",
                                valueKey: "name",
                            },
                        }
                    );

                    setSelectedFilters(() => _selectedFills);
                }
            },
            onError(_err) {
                setIsLoading(false);
            },
        }
    );

    const { data: reportTemps } = useQuery(
        ["report-templates"],
        () => getReportTemplates(user.id, "Meters List"),
        {
            onSuccess(res) {
                console.log(res, "response");
                setReportTemplates(() =>
                    res.data.rows.map((item: any) => ({
                        id: item.id,
                        key: item.key,
                        value: JSON.parse(item.value),
                    }))
                );
            },
        }
    );

    const handleMeter = (row: any) => {
        setActiveRow(row);
    };

    const handleClearAll = () => {
        // reset UI filters
        setIntermediateFilter(initialFilter);

        // reset applied filters
        setFilter(initialFilter);

        // notify child inputs
        setClearAll({
            hamlet: true,
            district: true,
            village: true,
            powerstation: true,
            search: true,
            property: true,
            connectionType: true,
            priceType: true,
            status: true,
        });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 500 });

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    hamlet: false,
                    district: false,
                    village: false,
                    powerstation: false,
                    search: false,
                    property: false,
                    connectionType: false,
                    priceType: false,
                    status: false,
                }),
            0
        );
    };

    useEffect(() => {
        if (changeCount) {
            setFilter({ ...filter, ...intermediateFilter });
        }
    }, [changeCount]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const _id = params.get("id");

        if (_id) {
            setMeterId(parseInt(_id));

            setIntermediateFilter({
                ...intermediateFilter,
                id: parseInt(_id),
            });

            setChangeCount(changeCount + 1);
        }
    }, [location.pathname]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                // height: mdDown ? "auto" : `calc(100vh - 96px)`,
            }}
        >
            <Card
                elevation={0}
                sx={{
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                }}
            >
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1.5}
                >
                    <Grid item xs={12} md="auto">
                        <Typography variant="h5" color="GrayText">
                            Meters
                        </Typography>

                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        container
                        spacing={1.5}
                        justifyContent="flex-end"
                    >
                        <Grid item flex={1}></Grid>

                        <Grid item xs={12} md="auto">
                            <MultiPermissionAuthorize ops={["CREATE METER"]}>
                                <Link
                                    to="/customer/meters/add"
                                    style={{ textDecoration: "none" }}
                                >
                                    <Button
                                        sx={{ height: "100%" }}
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Add />}
                                        fullWidth
                                    >
                                        add meter
                                    </Button>
                                </Link>
                            </MultiPermissionAuthorize>
                        </Grid>
                    </Grid>

                    <Box
                        sx={{
                            mt: 1.5,
                            mb: 3,
                            p: 1.5,
                            borderLeft: `4px solid ${theme.palette.secondary.main}`,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">
                        This report contains consumer connection and meter information,
    including ID, Meter Number, Customer Name, CNIC, Father's Name,
    Connection Charges, Connection Date, Unit Charge Rule, Property
    Details, Connection Type, Power Station, Meter Replacement Status,
    Meter Status, Unit Correction Settings, and Price Type.
                        </Typography>
                        <Typography variant="body2">
                        اس رپورٹ میں صارف کے کنکشن اور میٹر کی معلومات شامل ہیں، جن میں
    شناختی نمبر، میٹر نمبر، صارف کا نام، شناختی کارڈ نمبر، والد کا
    نام، کنکشن چارجز، کنکشن کی تاریخ، یونٹ چارج رول، پراپرٹی کی
    تفصیلات، کنکشن کی قسم، پاور اسٹیشن، میٹر تبدیلی کی حیثیت، میٹر
    کی حالت، یونٹ تصحیح کی ترتیبات اور قیمت کی قسم شامل ہیں۔
                        </Typography>
                    </Box>

                 

                    <Grid item container xs={12} spacing={1.5}>
                        <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                            <Grid item xs={12} sm={6} md={3}>
                                <UpdatedSearchableInput
                                    label="District"
                                    api="/region/district"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    clearAll={clearAll.district}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                            <Grid item xs={12} sm={6} md={3}>
                                <UpdatedSearchableInput
                                    label="Powerstation"
                                    api="/powerstation"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={intermediateFilter.district}
                                    params={{
                                        district: intermediateFilter.district,
                                    }}
                                    clearAll={clearAll.powerstation}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                            <Grid item xs={12} sm={6} md={3}>
                                <UpdatedSearchableInput
                                    label="Village"
                                    api="/region/village"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={[
                                        intermediateFilter.district,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={{
                                        district: intermediateFilter.district,
                                        powerstation:
                                            intermediateFilter.powerstation,
                                    }}
                                    clearAll={clearAll.village}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                            <Grid item xs={12} sm={6} md={3}>
                                <UpdatedSearchableInput
                                    label="hamlet"
                                    api="/region/hamlet"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={[
                                        intermediateFilter.village,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={{
                                        village: intermediateFilter.village,
                                        powerstation:
                                            intermediateFilter.powerstation,
                                    }}
                                    clearAll={clearAll.hamlet}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ PROPERTY"]}>
                            <Grid item xs={12} sm={6} md={3}>
                                <UpdatedSearchableInput
                                    api="/customer/property"
                                    label="Property"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={[
                                        intermediateFilter.district,
                                        intermediateFilter.village,
                                        intermediateFilter.hamlet,
                                        intermediateFilter.powerstation,
                                    ]}
                                    params={Object.fromEntries(
                                        Object.entries(
                                            intermediateFilter
                                        ).filter(
                                            ([k, v]) =>
                                                [
                                                    "district",
                                                    "village",
                                                    "hamlet",
                                                    "powerstation",
                                                ].includes(k) &&
                                                v &&
                                                v !== "all"
                                        )
                                    )}
                                    clearAll={clearAll.property}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="From Date"
                                name="connectionDateStart"
                                InputLabelProps={{ shrink: true }}
                                value={intermediateFilter.connectionDateStart}
                                type="date"
                                onChange={(e) =>
                                    setIntermediateFilter({
                                        ...intermediateFilter,
                                        connectionDateStart: e.target.value,
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="To  Date"
                                name="connectionDateEnd"
                                InputLabelProps={{ shrink: true }}
                                value={intermediateFilter.connectionDateEnd}
                                type="date"
                                onChange={(e) =>
                                    setIntermediateFilter({
                                        ...intermediateFilter,
                                        connectionDateEnd: e.target.value,
                                    })
                                }
                            />
                        </Grid>

                        <MultiPermissionAuthorize
                            ops={["READ CONNECTION_TYPE"]}
                        >
                            <Grid item xs={12} sm={6} md={3}>
                                <UpdatedSearchableInput
                                    label="Connection Type"
                                    _name="connectionType"
                                    api="/connection"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    multiple
                                    clearAll={clearAll.connectionType}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ PRICE_TYPE"]}>
                            <Grid item xs={12} sm={6} md={3}>
                                <UpdatedSearchableInput
                                    label="Price Type"
                                    _name="priceType"
                                    api="/priceType"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={Object.keys(intermediateFilter).filter(
                                        ([k, v]) =>
                                            [
                                                "district",
                                                "village",
                                                "hamlet",
                                                "powerstation",
                                            ].includes(k)
                                    )}
                                    params={Object.fromEntries(
                                        Object.entries(
                                            intermediateFilter
                                        ).filter(
                                            ([k, v]) =>
                                                [
                                                    "district",
                                                    "village",
                                                    "hamlet",
                                                    "powerstation",
                                                ].includes(k) &&
                                                v &&
                                                v !== "all"
                                        )
                                    )}
                                    clearAll={clearAll.priceType}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Status"
                                size="small"
                                select
                                fullWidth
                                value={intermediateFilter.status} // <-- controlled value
                                onChange={(e) =>
                                    setIntermediateFilter({
                                        ...intermediateFilter,
                                        status: e.target.value as any,
                                    })
                                }
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="replaced">Replaced</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <SearchBar
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={1.4}>
                            <Button
                                color="secondary"
                                variant="outlined"
                                onClick={() => setChangeCount(changeCount + 1)}
                                sx={{ height: "100%" }}
                                startIcon={<FilterAltOutlined />}
                                fullWidth
                            >
                                Search
                            </Button>
                        </Grid>

                        {/* CLEAR ALL */}
                        <Grid item xs={12} sm={6} md={1.4}>
                            <Button
                                color="error"
                                variant="outlined"
                                startIcon={<FilterAltOffOutlined />}
                                onClick={handleClearAll}
                                sx={{
                                    height: "100%",
                                    minWidth: theme.spacing(20),
                                }}
                                fullWidth
                            >
                                Clear All
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>

            {(!changeCount || !data?.data.rows.length || isError) && (
                <Alert severity="info">
                    {!data?.data.rows.length && isSuccess
                        ? noDataMessage
                        : !hasValue(filter) && !changeCount
                            ? filterSelectionMessage
                            : filterSelectionMessage}
                </Alert>
            )}

            <Box sx={{ height: "auto" }}>
                <BaseTable
                    load={Boolean(changeCount)}
                    headers={{
                        id: "ID",
                        meterNo: "Meter No",
                        customer: "Customer",
                        cnic: "CNIC",
                        fatherName: "Father Name",
                        connectionCharges: "Connection Charges",
                        connectionDate: "Connection Date",
                        property: "Property",
                        connectionType: "Connection Type",
                        priceType: "Price Type",
                        powerstation: "Powerstation",
                        lineRent: "Line Rent",
                        surcharge: "Surcharge",
                        credit: "Credit Amount",
                        district: "District",
                        village: "Village",
                        hamlet: "Hamlet",
                        view: isReplaceMeterOps ? "Replace Meter" : "",
                        status: "Meter Status",
                        prevMeter: "Previous Meter",
                        unitCorrection: isUnitCorrectionOps
                            ? "Unit correction"
                            : "",
                        unitChargeRule: "Unit Charge Rule",
                        customerId: "customerId",
                        powerstationId: "powerstationId",
                    }}
                    defaultSelectedHeaders={[
                        "id",
                        "meterNo",
                        "customer",
                        "cnic",
                        "fatherName",
                        "connectionCharges",
                        "connectionDate",
                        "unitChargeRule",
                        "property",
                        "connectionType",
                        "powerstation",
                        "view",
                        "status",
                        "unitCorrection",
                    ]}
                    _printables={[
                        "id",
                        "meterNo",
                        "customer",
                        "connectionCharges",
                        "connectionDate",
                        "property",
                        "connectionType",
                        "priceType",
                        "powerstation",
                        "lineRent",
                        "surcharge",
                        "unitChargeRule",
                        "district",
                        "village",
                        "hamlet",
                        "status",
                        "prevMeter",
                        "credit",
                    ]}
                    data={data?.data.rows.map((row: any) => ({
                        ...row,
                        view:
                            isReplaceMeterOps &&
                            (row.status === "active" ? (
                                <IconButton onClick={() => handleMeter(row)}>
                                    <AutorenewIcon />
                                </IconButton>
                            ) : (
                                <IconButton disabled>
                                    <AutorenewIcon />
                                </IconButton>
                            )),
                        property: row.property?.name,
                        cnic: row?.property?.customer?.cnic,
                        fatherName: row?.property?.customer?.fatherName,
                        connectionType: row.connectionType?.name,
                        priceType: row.priceType?.name,
                        powerstation: row.powerstation?.name,
                        customer: row.property?.customer?.name,
                        district: row.district?.name,
                        village: row.village?.name,
                        hamlet: row.hamlet?.name,
                        unitChargeRule: (
                            <Tooltip title={row?.priceType?.unitChargeRule?.name}>
                                <IconButton
                                    onClick={() =>
                                        handleViewRule(
                                            row?.priceType?.unitChargeRule
                                        )
                                    }
                                    size="small"
                                >
                                    <VisibilityOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ),
                        connectionCharges: currencyFormatter.format(
                            row.connectionCharges
                        ),
                        lineRent: currencyFormatter.format(
                            row?.priceType?.lineRent?.amount
                        ),
                        surcharge: row?.priceType?.surcharge?.isSurchargePct
                            ? row.priceType?.surcharge?.value + " %"
                            : currencyFormatter.format(
                                row.priceType?.surcharge?.value
                            ),
                        status: (
                            <>
                                <Chip
                                    label={row.status.toUpperCase()}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        fontWeight: "bold",
                                        bgcolor:
                                            row.status === "active"
                                                ? green[50]
                                                : row.status === "replaced"
                                                    ? purple[50]
                                                    : grey[200],
                                        color:
                                            row.status === "active"
                                                ? green[500]
                                                : row.status === "replaced"
                                                    ? purple[500]
                                                    : grey[500],
                                        border: "none",
                                        width: "13ch",
                                    }}
                                />
                            </>
                        ),

                        unitCorrection: isUnitCorrectionOps && (
                            <IconButton
                                onClick={() => setUnitCorrectionOpen(row)}
                            >
                                <TuneOutlined />
                            </IconButton>
                        ),

                        prevMeter: row.prevMeter ? (
                            <>
                                <Button
                                    color="info"
                                    onClick={() => setShowPrevMeter(true)}
                                >
                                    {row.prevMeter.meterNo}
                                </Button>
                                <PreviousMeter
                                    open={showPrevMeter}
                                    setOpen={setShowPrevMeter}
                                    meter={row.prevMeter}
                                />
                            </>
                        ) : (
                            <Button fullWidth disabled={!row.prevMeter}>
                                <HorizontalRuleOutlined />
                            </Button>
                        ),
                        customerId: row?.property?.customer?.id,
                        powerstationId: row?.powerstation?.id,
                    }))}
                    reportName="Meters__List"
                    reportDetails={{
                        district: filter.district ? filter.district : undefined,
                        powerstation: filter.powerstation
                            ? filter.powerstation
                            : undefined,
                        village: filter.village ? filter.village : undefined,
                        hamlet: filter.hamlet ? filter.hamlet : undefined,
                    }}
                    delEndPoint="/customer/meter"
                    rowsCount={rowsCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    isLoading={isLoading}
                    feedback={feedback}
                    setFeedback={setFeedback}
                    permissions={{
                        edit: ["UPDATE METER"],
                        delete: ["DELETE METER"],
                    }}
                    queryKey={[
                        "meter",
                        pagination.page,
                        pagination.limit,
                        filter,
                    ]}
                    hasReportTemplates
                    reportTemplates={reportTemplates}
                    width={1366}
                />

                <MultiPermissionAuthorize ops={["REPLACE METER"]}>
                    {Boolean(activeRow) && (
                        <MeterReplacement
                            activeRow={activeRow}
                            setActiveRow={setActiveRow}
                        />
                    )}
                </MultiPermissionAuthorize>

                <MultiPermissionAuthorize ops={["UNIT CORRECTION"]}>
                    {" "}
                    {Boolean(unitCorrectionOpen) && (
                        <UnitCorrection
                            meter={unitCorrectionOpen}
                            open={Boolean(unitCorrectionOpen)}
                            close={() => setUnitCorrectionOpen(null)}
                            setUnitCorrectionOpen={setUnitCorrectionOpen}
                        />
                    )}
                </MultiPermissionAuthorize>

                {Boolean(activeUnitchargeRuleRow) && (
                    <RuleView
                        activeRow={activeUnitchargeRuleRow}
                        setActiveRow={setActiveUnitChargeRuleRow}
                    />
                )}
            </Box>
        </Box>
    );
};

export default Meter;
